import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CalendarClock, Plus, Stethoscope, ScanLine, Loader2, CheckCircle2, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

const statusBadge = (a) => {
  if (a.status === 'done') return <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">Done</Badge>;
  if (a.status === 'cancelled') return <Badge variant="outline" className="text-[10px]">Cancelled</Badge>;
  if (a.date && new Date(a.date) < new Date(new Date().toDateString())) return <Badge className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">Needs update</Badge>;
  return <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">Upcoming</Badge>;
};

export const AppointmentTracker = ({ appointments = [], conditions = [], currentConditions = [], onChanged }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [editAppt, setEditAppt] = useState(null);
  const [outcomeAppt, setOutcomeAppt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: 'scan', mode: 'date', date: '', week: '' });
  const [outcome, setOutcome] = useState({ reports_normal: null, notes: '', checked: [] });

  const resetForm = () => setForm({ type: 'scan', mode: 'date', date: '', week: '' });

  const addAppt = async () => {
    setBusy(true);
    try {
      await api.post('/api/prenatal-coach/appointments', {
        type: form.type,
        date: form.mode === 'date' ? form.date : null,
        is_tentative: form.mode !== 'date',
        tentative_week: form.mode === 'week' ? parseInt(form.week, 10) : null,
      });
      toast.success('Appointment added');
      setAddOpen(false); resetForm(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    setBusy(false);
  };

  const reschedule = async () => {
    setBusy(true);
    try {
      await api.put(`/api/prenatal-coach/appointments/${editAppt.id || editAppt._id}`, {
        date: form.mode === 'date' ? form.date : null,
        is_tentative: form.mode !== 'date',
        tentative_week: form.mode === 'week' ? parseInt(form.week, 10) : null,
      });
      toast.success('Appointment updated');
      setEditAppt(null); resetForm(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    setBusy(false);
  };

  const saveOutcome = async () => {
    setBusy(true);
    try {
      // V66 — the checklist is the source of truth: ticked-but-new → added, unticked-but-current → removed
      const added = outcome.checked.filter((s) => !currentConditions.includes(s));
      const removed = currentConditions.filter((s) => !outcome.checked.includes(s));
      await api.post(`/api/prenatal-coach/appointments/${outcomeAppt.id || outcomeAppt._id}/outcome`, {
        reports_normal: outcome.reports_normal,
        notes: outcome.notes || null,
        conditions_added: added,
        conditions_removed: removed,
      });
      toast.success(added.length || removed.length ? 'Thanks! Your plan and playlist have been updated.' : 'Thanks! Your plan has been recalibrated.');
      setOutcomeAppt(null);
      setOutcome({ reports_normal: null, notes: '', checked: [] });
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setBusy(false);
  };

  const toggleChecked = (slug) => setOutcome((o) => ({
    ...o, checked: o.checked.includes(slug) ? o.checked.filter((s) => s !== slug) : [...o.checked, slug],
  }));
  const openOutcome = (a) => { setOutcome({ reports_normal: null, notes: '', checked: [...currentConditions] }); setOutcomeAppt(a); };

  const open = appointments.filter((a) => a.status === 'upcoming');
  const past = appointments.filter((a) => a.status !== 'upcoming').slice(-3);

  return (
    <Card className="rounded-2xl" data-testid="appointment-tracker">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading text-base flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />Appointments & Scans</CardTitle>
        <Button size="sm" variant="outline" onClick={() => { resetForm(); setAddOpen(true); }} data-testid="add-appointment-btn">
          <Plus className="h-3.5 w-3.5 mr-1" />Add
        </Button>
      </CardHeader>
      <CardContent className="space-y-2 pt-2">
        {open.length === 0 && past.length === 0 && (
          <p className="text-xs text-muted-foreground py-2">No appointments yet — add your next scan or doctor visit so I can plan around it.</p>
        )}
        {[...open, ...past].map((a) => (
          <div key={a.id || a._id} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border" data-testid={`appointment-row-${a.id || a._id}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              {a.type === 'scan' ? <ScanLine className="h-4 w-4 text-primary shrink-0" /> : <Stethoscope className="h-4 w-4 text-primary shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{a.title || (a.type === 'scan' ? 'Scan' : 'Doctor visit')}</p>
                <p className="text-xs text-muted-foreground">
                  {a.date ? formatDate(a.date) : a.tentative_week ? `Around week ${a.tentative_week}` : 'Date not decided yet'}
                  {a.is_tentative && a.date ? ' (tentative)' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {statusBadge(a)}
              {a.status === 'upcoming' && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 text-xs px-2" data-testid={`reschedule-appt-${a.id || a._id}`}
                    onClick={() => { setForm({ type: a.type, mode: a.date ? 'date' : 'week', date: a.date || '', week: a.tentative_week || '' }); setEditAppt(a); }}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" data-testid={`outcome-appt-${a.id || a._id}`}
                    onClick={() => openOutcome(a)}>
                    <CheckCircle2 className="h-3 w-3 mr-1" />Done
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      {/* Add / Edit dialog */}
      <Dialog open={addOpen || !!editAppt} onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditAppt(null); } }}>
        <DialogContent className="max-w-md" data-testid="appointment-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editAppt ? 'Update appointment' : 'Add appointment'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editAppt && (
              <div className="flex gap-2">
                {['scan', 'doctor_visit'].map((t) => (
                  <button key={t} type="button" onClick={() => setForm((f) => ({ ...f, type: t }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${form.type === t ? 'bg-primary/10 border-primary text-primary' : 'border-border'}`}>
                    {t === 'scan' ? 'Scan' : 'Doctor visit'}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              {[['date', 'Exact date'], ['week', 'Rough week'], ['unsure', 'Not sure yet']].map(([m, label]) => (
                <button key={m} type="button" onClick={() => setForm((f) => ({ ...f, mode: m }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${form.mode === m ? 'bg-primary/10 border-primary text-primary' : 'border-border'}`}>
                  {label}
                </button>
              ))}
            </div>
            {form.mode === 'date' && <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="appt-form-date" />}
            {form.mode === 'week' && <Input type="number" min="4" max="42" placeholder="Around pregnancy week…" value={form.week} onChange={(e) => setForm((f) => ({ ...f, week: e.target.value }))} data-testid="appt-form-week" />}
          </div>
          <DialogFooter>
            <Button onClick={editAppt ? reschedule : addAppt}
              disabled={busy || (form.mode === 'date' && !form.date) || (form.mode === 'week' && !form.week)}
              data-testid="appt-form-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outcome dialog */}
      <Dialog open={!!outcomeAppt} onOpenChange={(v) => !v && setOutcomeAppt(null)}>
        <DialogContent className="max-w-md" data-testid="appointment-outcome-dialog">
          <DialogHeader><DialogTitle className="font-heading">How did it go?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Were your reports normal?</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOutcome((o) => ({ ...o, reports_normal: true }))}
                className={`flex-1 px-3 py-2 rounded-xl border text-sm font-medium ${outcome.reports_normal === true ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-border'}`}
                data-testid="outcome-normal-yes">Yes, all normal</button>
              <button type="button" onClick={() => setOutcome((o) => ({ ...o, reports_normal: false }))}
                className={`flex-1 px-3 py-2 rounded-xl border text-sm font-medium ${outcome.reports_normal === false ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-border'}`}
                data-testid="outcome-normal-no">Something came up</button>
            </div>
            {outcome.reports_normal !== null && (
              <div data-testid="outcome-conditions-checklist">
                <p className="text-xs text-muted-foreground mb-1.5">
                  {currentConditions.length
                    ? 'These are on your profile now — untick anything your doctor has cleared, tick anything new. Your playlist updates instantly.'
                    : 'Did your doctor mention any of these? Tick what applies — your playlist updates instantly.'}
                </p>
                <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto">
                  {[...conditions].sort((a, b) => Number(currentConditions.includes(b.slug)) - Number(currentConditions.includes(a.slug))).map((c) => {
                    const on = outcome.checked.includes(c.slug);
                    const was = currentConditions.includes(c.slug);
                    return (
                      <button key={c.slug} type="button" onClick={() => toggleChecked(c.slug)}
                        data-testid={`outcome-cond-${c.slug}`} aria-pressed={on}
                        className={`text-left px-2 py-1.5 rounded-lg border text-[11px] font-medium flex items-center gap-1.5 ${on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                        {on ? <CheckSquare className="h-3.5 w-3.5 shrink-0" /> : <Square className="h-3.5 w-3.5 shrink-0" />}
                        <span className="flex-1">{c.label}</span>
                        {was && !on && <span className="text-[9px] text-emerald-700">cleared</span>}
                        {!was && on && <span className="text-[9px] text-amber-700">new</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <Textarea placeholder="Anything else you'd like me to know? (optional)" value={outcome.notes}
              onChange={(e) => setOutcome((o) => ({ ...o, notes: e.target.value }))} className="text-sm" rows={2} data-testid="outcome-notes" />
          </div>
          <DialogFooter>
            <Button onClick={saveOutcome} disabled={busy || outcome.reports_normal === null} data-testid="outcome-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update my plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
